import React, { useState, useEffect } from 'react';
import { X, Package, DollarSign, AlertCircle } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import type { SlotCreate } from '../types';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

/**
 * Slot Form Modal Props
 */
interface SlotFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  fairId: number;
  onSuccess: () => void;
}

/**
 * Slot Form Modal Component
 *
 * @component
 * @description Modal form for creating booth slots within a fair. Used in Fair Manager Slot Management interface.
 *
 * ## E2E Testing
 *
 * ### Required Fields (form won't submit without these):
 * - `slotNumber` (`#slotNumber`) - Slot identifier, max 50 chars, must be unique within fair
 * - `slotType` (`#slotType`) - Slot category (common, preferred, premium, vip)
 * - `basePrice` (`#basePrice`) - Daily rental price, min 0, step 0.01
 *
 * ### Optional Fields:
 * - `dimensions` (`#dimensions`) - Physical size, max 100 chars (e.g., "10x10 feet")
 * - `hasPower` (`#hasPower`) - Checkbox for electrical power access
 * - `isAvailable` (`#isAvailable`) - Checkbox for booking availability (defaults to true)
 *
 * ### Validation Rules:
 * - Slot number: Required, max 50 chars
 * - Base price: Required, must be valid positive number
 * - Slot type: Required, must be one of: common, preferred, premium, vip
 *
 * ### Slot Types:
 * - `common` - Standard booth slot
 * - `preferred` - Better location or features
 * - `premium` - High-visibility location
 * - `vip` - Best available booth slot
 *
 * @example
 * // E2E Test Example - Create Slot
 * await page.click('[data-testid="add-slot-button"]'); // Open modal
 * await page.fill('#slotNumber', 'A1');
 * await page.selectOption('#slotType', 'premium');
 * await page.fill('#dimensions', '10x10 feet');
 * await page.fill('#basePrice', '150.00');
 * await page.check('#hasPower');
 * await page.click('button[type="submit"]:has-text("Create Slot")');
 * await page.waitForSelector('[data-testid="add-slot-button"]'); // Modal closed
 *
 * @see {@link ../../../tests/e2e/docs/FORM_FIELDS_REFERENCE.md#slotformmodal} - Complete field documentation
 * @see {@link Issue #990} - Form validation rules documentation
 */

const SLOT_TYPES = [
  { value: 'common', label: 'Common' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'premium', label: 'Premium' },
  { value: 'vip', label: 'VIP' },
];

const SlotFormModal: React.FC<SlotFormModalProps> = ({
  isOpen,
  onClose,
  fairId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [slotNumber, setSlotNumber] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [hasPower, setHasPower] = useState(false);
  const [slotType, setSlotType] = useState('common');
  const [basePrice, setBasePrice] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);

  // Validation errors
  const [slotNumberError, setSlotNumberError] = useState('');
  const [basePriceError, setBasePriceError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    // Reset form for new slot
    setSlotNumber('');
    setDimensions('');
    setHasPower(false);
    setSlotType('common');
    setBasePrice('');
    setIsAvailable(true);
    setError(null);
    setSlotNumberError('');
    setBasePriceError('');
  }, [isOpen]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Slot number validation
    if (!slotNumber.trim()) {
      setSlotNumberError('Slot number is required');
      isValid = false;
    } else if (slotNumber.length > 50) {
      setSlotNumberError('Slot number must be 50 characters or less');
      isValid = false;
    } else {
      setSlotNumberError('');
    }

    // Base price validation
    if (!basePrice.trim()) {
      setBasePriceError('Base price is required');
      isValid = false;
    } else {
      const price = parseFloat(basePrice);
      if (isNaN(price) || price < 0) {
        setBasePriceError('Price must be a valid positive number');
        isValid = false;
      } else {
        setBasePriceError('');
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: SlotCreate = {
        fair_id: fairId,
        slot_number: slotNumber.trim(),
        dimensions: dimensions.trim() || undefined,
        has_power: hasPower,
        slot_type: slotType,
        base_price: parseFloat(basePrice),
        is_available: isAvailable
      };

      await fairManagerApi.createSlot(fairId, data);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to create slot:', err);
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-secondary" />
            <h2 className="text-xl font-semibold text-gray-900">
              Add New Slot
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Slot Number */}
          <div>
            <label htmlFor="slotNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Slot Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="slotNumber"
              value={slotNumber}
              onChange={(e) => {
                setSlotNumber(e.target.value);
                setSlotNumberError('');
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                slotNumberError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., A1, B12, Booth-101"
              maxLength={50}
              disabled={loading}
              data-testid="slot-slotNumber-input"
            />
            {slotNumberError && <p className="mt-1 text-sm text-red-600">{slotNumberError}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier for this booth slot (must be unique within this fair)
            </p>
          </div>

          {/* Slot Type */}
          <div>
            <label htmlFor="slotType" className="block text-sm font-medium text-gray-700 mb-1">
              Slot Type <span className="text-red-500">*</span>
            </label>
            <select
              id="slotType"
              value={slotType}
              onChange={(e) => setSlotType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
              data-testid="slot-slotType-select"
            >
              {SLOT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Category of booth slot (affects pricing and vendor preferences)
            </p>
          </div>

          {/* Dimensions */}
          <div>
            <label htmlFor="dimensions" className="block text-sm font-medium text-gray-700 mb-1">
              Dimensions
            </label>
            <input
              type="text"
              id="dimensions"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 10x10 feet, 8x12 feet"
              maxLength={100}
              disabled={loading}
              data-testid="slot-dimensions-input"
            />
            <p className="mt-1 text-xs text-gray-500">
              Physical size of the booth space
            </p>
          </div>

          {/* Base Price */}
          <div>
            <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
              Base Price (per day) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                id="basePrice"
                value={basePrice}
                onChange={(e) => {
                  setBasePrice(e.target.value);
                  setBasePriceError('');
                }}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  basePriceError ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={loading}
                data-testid="slot-basePrice-input"
              />
            </div>
            {basePriceError && <p className="mt-1 text-sm text-red-600">{basePriceError}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Daily rental price for this booth slot
            </p>
          </div>

          {/* Has Power */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="hasPower"
              checked={hasPower}
              onChange={(e) => setHasPower(e.target.checked)}
              className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
              disabled={loading}
              data-testid="slot-hasPower-checkbox"
            />
            <div className="flex-1">
              <label htmlFor="hasPower" className="block text-sm font-medium text-gray-700">
                Has Electrical Power
              </label>
              <p className="text-xs text-gray-500">
                Check if this booth has access to electrical outlets
              </p>
            </div>
          </div>

          {/* Is Available */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="isAvailable"
              checked={isAvailable}
              onChange={(e) => setIsAvailable(e.target.checked)}
              className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
              disabled={loading}
              data-testid="slot-isAvailable-checkbox"
            />
            <div className="flex-1">
              <label htmlFor="isAvailable" className="block text-sm font-medium text-gray-700">
                Available for Booking
              </label>
              <p className="text-xs text-gray-500">
                Uncheck to temporarily make this slot unavailable for reservations
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
              data-testid="cancel-slot-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
              disabled={loading}
              data-testid="submit-slot-button"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{loading ? 'Creating...' : 'Create Slot'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SlotFormModal;
